import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import transformStructuresToDoseTargets from '~/utils/transforms/doseTargetsTransforms';

const useDoseTargetData = () => {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const [dosePredictionTask, setDosePredictionTask] = useRecoilState(
        dosePredictionTaskState
    );
    const targets = useMemo(() => {
        return currentStructureSet?.roiArray
            ? transformStructuresToDoseTargets(currentStructureSet.roiArray)
            : [];
    }, [currentStructureSet]);

    useEffect(() => {
        setDosePredictionTask((prev) => ({
            ...prev,
            targets,
        }));
    }, [targets, setDosePredictionTask]);

    const handleTargetChange = (index: number) => {
        setDosePredictionTask((prev) => {
            if (!prev.targets) return prev;

            const updatedTargets = [...prev.targets];
            const isTarget = !updatedTargets[index].isTarget;

            updatedTargets[index] = {
                ...updatedTargets[index],
                isTarget,
                doseQuantity: isTarget
                    ? Math.max(updatedTargets[index].doseQuantity, 30) // Ensure min dose of 1 Gy
                    : 0.0, // Reset to 0 if not a target
            };

            return { ...prev, targets: updatedTargets };
        });
    };

    const handleDoseQuantityChange = (index: number, value: number | null) => {
        setDosePredictionTask((prev) => {
            if (!prev.targets || !value) return prev;

            const updatedTargets = [...prev.targets];
            updatedTargets[index] = {
                ...updatedTargets[index],
                doseQuantity: value,
            };

            return { ...prev, targets: updatedTargets };
        });
    };

    return {
        doseTargetData: dosePredictionTask?.targets || [],
        handleTargetChange,
        handleDoseQuantityChange,
    };
};

export default useDoseTargetData;
